import { describeError, useApi, useAuthState } from '@classgo/app-kit';
import { Button, Input, Modal, useToast } from '@classgo/ui';
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { staffApi } from '../api/staff-api';
import { config } from '../config';
import { useT } from '../i18n';

/**
 * Builds the school app URL that carries the handover in its fragment: never sent to a server,
 * consumed once by the school app. The session ends when the act-as session or the staff token
 * expires, whichever comes first.
 *
 * @param {object} handover -- Session id, tenant, the staff ID token and its expiry.
 * @returns The URL to open.
 */
export const actAsUrl = (handover: {
  sessionId: string;
  tenantId: string;
  idToken: string;
  email: string;
  expiresAt: number;
}): string => {
  const params = new URLSearchParams({
    session: handover.sessionId,
    tenant: handover.tenantId,
    token: handover.idToken,
    email: handover.email,
    expires: String(handover.expiresAt),
  });
  return `${config.schoolAppUrl}/act-as#${params.toString()}`;
};

/**
 * "Entrar como ClassGO": asks the reason (audited inside the school), opens the act-as session
 * and the school app in a new tab.
 */
export const ActAsButton = ({ tenant }: { tenant: { id: string; name: string } }) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const { tokens, email } = useAuthState();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (value: string) => staffApi(api).openActAs(tenant.id, value),
    onSuccess: (opened) => {
      if (!tokens || !email) return;
      const expiresAt = Math.min(tokens.expiresAt, Math.floor(Date.parse(opened.expiresAt) / 1000));
      window.open(
        actAsUrl({
          sessionId: opened.sessionId,
          tenantId: tenant.id,
          idToken: tokens.idToken,
          email,
          expiresAt,
        }),
        '_blank',
        'noopener',
      );
      setOpen(false);
      setReason('');
      toast.show({ tone: 'success', title: t('actAs.opened', { tenant: tenant.name }) });
    },
    onError: (thrown) => setError(describeError(t, thrown)),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    mutation.mutate(reason.trim());
  };

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {t('tenant.actions.actAs')}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('actAs.title')}
        description={t('actAs.body', { tenant: tenant.name })}
        actions={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="act-as-form" loading={mutation.isPending}>
              {t('actAs.open')}
            </Button>
          </>
        }
      >
        <form id="act-as-form" onSubmit={submit} className="space-y-4">
          {error ? (
            <p
              role="alert"
              className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
            >
              {error}
            </p>
          ) : null}
          <Input
            label={t('actAs.reason')}
            help={t('actAs.reason.help')}
            required
            minLength={3}
            maxLength={300}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </form>
      </Modal>
    </>
  );
};
